import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  Building2,
  Calendar,
  MapPin,
  Recycle,
  RefreshCw,
  Users,
  ClipboardList,
  CheckCircle2,
  Clock,
  ArrowRight,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { usePickups } from '@/hooks/queries/usePickups'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn, getStatusColor, getStatusDotColor } from '@/lib/utils'
import type { UserRole } from '@/types'

interface ShortcutItem {
  label: string
  description: string
  to: string
  icon: React.ElementType
  iconColor: string
  iconBg: string
  roles: UserRole[]
}

const shortcuts: ShortcutItem[] = [
  {
    label: 'Agenda de retiros',
    description: 'Ver y gestionar retiros programados',
    to: '/pickups',
    icon: Calendar,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100',
    roles: ['admin', 'operator'],
  },
  {
    label: 'Registrar retiro',
    description: 'Cargar un nuevo registro de retiro',
    to: '/records/new',
    icon: ClipboardList,
    iconColor: 'text-green-600',
    iconBg: 'bg-green-100',
    roles: ['admin'],
  },
  {
    label: 'Clientes',
    description: 'Administrar clientes y sus datos',
    to: '/clients',
    icon: Building2,
    iconColor: 'text-orange-600',
    iconBg: 'bg-orange-100',
    roles: ['admin'],
  },
  {
    label: 'Tipos de residuo',
    description: 'Gestionar categorías de residuos',
    to: '/waste-types',
    icon: Recycle,
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
    roles: ['admin'],
  },
  {
    label: 'Disposiciones finales',
    description: 'Administrar destinos de valorización',
    to: '/destinations',
    icon: MapPin,
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-100',
    roles: ['admin'],
  },
  {
    label: 'Usuarios',
    description: 'Gestionar usuarios del sistema',
    to: '/users',
    icon: Users,
    iconColor: 'text-slate-600',
    iconBg: 'bg-slate-100',
    roles: ['admin'],
  },
  {
    label: 'Sincronización',
    description: 'Revisar estado de sincronización',
    to: '/sync',
    icon: RefreshCw,
    iconColor: 'text-cyan-600',
    iconBg: 'bg-cyan-100',
    roles: ['admin', 'operator'],
  },
]

function TodayPickupsPanel() {
  const navigate = useNavigate()
  const today = format(new Date(), 'yyyy-MM-dd')

  const { data: pickups = [], isLoading } = usePickups({
    date_from: today,
    date_to: today,
  })

  const completed = pickups.filter((p) => p.status === 'completed')
  const pending = pickups.filter((p) => p.status !== 'completed' && p.status !== 'cancelled')
  const cancelled = pickups.filter((p) => p.status === 'cancelled')

  const todayLabel = format(new Date(), "EEEE d 'de' MMMM", { locale: es })

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base capitalize">{todayLabel}</CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">Retiros de hoy</p>
          </div>
          <button
            onClick={() => navigate('/pickups')}
            className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-gray-900"
          >
            Ver agenda
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Contadores */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg bg-green-50 p-3 text-center">
            <CheckCircle2 className="mx-auto mb-1 h-4 w-4 text-green-600" />
            <p className="text-xl font-bold text-green-700">{completed.length}</p>
            <p className="text-[11px] text-green-600">Completados</p>
          </div>
          <div className="rounded-lg bg-amber-50 p-3 text-center">
            <Clock className="mx-auto mb-1 h-4 w-4 text-amber-600" />
            <p className="text-xl font-bold text-amber-700">{pending.length}</p>
            <p className="text-[11px] text-amber-600">Pendientes</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 text-center">
            <p className="mb-1 text-base font-bold leading-none text-gray-400">—</p>
            <p className="text-xl font-bold text-gray-600">{pickups.length}</p>
            <p className="text-[11px] text-gray-500">Total</p>
          </div>
        </div>

        {/* Lista */}
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-gray-100" />
            ))}
          </div>
        ) : pickups.length === 0 ? (
          <p className="py-2 text-center text-sm text-muted-foreground">
            No hay retiros programados para hoy
          </p>
        ) : (
          <ul className="space-y-2">
            {[...pending, ...completed, ...cancelled].map((pickup) => (
              <li
                key={pickup.id}
                className="flex cursor-pointer items-center justify-between gap-2 rounded-md border px-3 py-2 transition-colors hover:border-[#b9d94a]"
                onClick={() => navigate(`/pickups/${pickup.id}`)}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn('h-2 w-2 flex-shrink-0 rounded-full', getStatusDotColor(pickup.status))}
                  />
                  <span className="truncate text-sm font-medium text-gray-900">
                    {pickup.client?.trade_name ?? `Retiro #${pickup.id}`}
                  </span>
                </div>
                <Badge className={cn('shrink-0 text-[10px]', getStatusColor(pickup.status))}>
                  {pickup.status_label}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

export function HomePage() {
  const { role, user } = useAuth()
  const navigate = useNavigate()

  const visibleShortcuts = shortcuts.filter(
    (s) => role && s.roles.includes(role)
  )
  const showTodayPanel = role === 'admin' || role === 'operator'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Bienvenido, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-sm text-muted-foreground">¿Qué deseas hacer hoy?</p>
      </div>

      <div className={cn('gap-6', showTodayPanel ? 'grid lg:grid-cols-[1fr_320px]' : '')}>
        {/* Accesos directos */}
        <div className="grid auto-rows-max gap-4 sm:grid-cols-2">
          {visibleShortcuts.map((shortcut) => (
            <Card
              key={shortcut.to}
              className="cursor-pointer transition-shadow hover:shadow-md"
              onClick={() => navigate(shortcut.to)}
            >
              <CardContent className="flex items-center gap-4 p-6">
                <div className={`rounded-full p-3 ${shortcut.iconBg}`}>
                  <shortcut.icon className={`h-6 w-6 ${shortcut.iconColor}`} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{shortcut.label}</p>
                  <p className="text-sm text-muted-foreground">{shortcut.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Panel de hoy */}
        {showTodayPanel && <TodayPickupsPanel />}
      </div>

      <p className="text-xs text-gray-400">v{__APP_VERSION__}</p>
    </div>
  )
}
